import { useEffect, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';

const API = 'https://guardiancloudcare.com/backend/api';

type ActivityType =
  | 'home_visit'
  | 'interview'
  | 'intervention'
  | 'follow_up'
  | 'assessment'
  | 'coordination'
  | 'meeting'
  | 'documentation'
  | 'other';

interface CaseItem {
  case_number: string;
  full_name?: string;
  child_name?: string;
  name?: string;
  case_status?: string;
  incident_type?: string;
}

interface Schedule {
  id: number;
  case_uid?: string;
  case_number: string;
  social_worker_uid?: string;
  victim_uid?: string;

  activity_type: string;
  title: string;
  description?: string;

  scheduled_date: string;
  start_time?: string;
  end_time?: string;

  location?: string;

  status: string;
  notes?: string;

  created_at?: string;
  updated_at?: string;

  child_name?: string;
}

interface ScheduleForm {
  caseNumber: string;
  childName: string;

  activityType: ActivityType;
  customActivityType: string;

  title: string;
  description: string;

  date: string;
  startTime: string;
  endTime: string;

  location: string;
  notes: string;
}

const activityTypeLabels: Record<
  Exclude<ActivityType, 'other'>,
  string
> = {
  home_visit: 'Home Visit',
  interview: 'Interview',
  intervention: 'Intervention',
  follow_up: 'Follow-up',
  assessment: 'Assessment',
  coordination: 'Coordination',
  meeting: 'Meeting',
  documentation: 'Documentation',
};

const activityTypeIcons: Record<
  ActivityType,
  string
> = {
  home_visit: 'ri-home-heart-line',
  interview: 'ri-chat-3-line',
  intervention: 'ri-hand-heart-line',
  follow_up: 'ri-loop-right-line',
  assessment: 'ri-shield-check-line',
  coordination: 'ri-team-line',
  meeting: 'ri-group-line',
  documentation: 'ri-file-text-line',
  other: 'ri-more-line',
};

const scheduleStatusStyles: Record<
  string,
  string
> = {
  scheduled:
    'bg-blue-100 text-blue-700 border border-blue-200',

  completed:
    'bg-green-100 text-green-700 border border-green-200',

  cancelled:
    'bg-red-100 text-red-700 border border-red-200',

  rescheduled:
    'bg-amber-100 text-amber-700 border border-amber-200',
};

function normalizeStatus(
  value?: string
) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function formatStatus(
  status?: string
) {
  if (!status) {
    return 'Scheduled';
  }

  return status
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) =>
      char.toUpperCase()
    );
}

function formatActivityType(
  value?: string
) {
  if (!value) {
    return 'Activity';
  }

  const normalized =
    normalizeStatus(value);

  const match =
    Object.entries(
      activityTypeLabels
    ).find(
      ([key]) =>
        normalizeStatus(key) ===
        normalized
    );

  if (match) {
    return match[1];
  }

  if (
    normalized === 'other'
  ) {
    return 'Other';
  }

  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) =>
      char.toUpperCase()
    );
}

function getActivityIcon(
  value?: string
) {
  const normalized =
    normalizeStatus(value);

  const match =
    Object.entries(
      activityTypeIcons
    ).find(
      ([key]) =>
        normalizeStatus(key) ===
        normalized
    );

  return (
    match?.[1] ||
    activityTypeIcons.other
  );
}

function getStatusStyle(
  status?: string
) {
  const normalized =
    normalizeStatus(status).replace(
      /\s+/g,
      '_'
    );

  return (
    scheduleStatusStyles[
      normalized
    ] ||
    'bg-gray-100 text-gray-700 border border-gray-200'
  );
}

function formatDate(
  value?: string
) {
  if (!value) {
    return 'Not available';
  }

  const date =
    new Date(
      `${value}T00:00:00`
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return value;
  }

  return date.toLocaleDateString(
    'en-US',
    {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }
  );
}

function formatTime(
  value?: string
) {
  if (!value) {
    return '';
  }

  const parts =
    value.split(':');

  if (parts.length < 2) {
    return value;
  }

  const hour =
    Number(parts[0]);

  const minute =
    parts[1];

  if (
    Number.isNaN(hour)
  ) {
    return value;
  }

  const suffix =
    hour >= 12
      ? 'PM'
      : 'AM';

  const displayHour =
    hour % 12 || 12;

  return `${displayHour}:${minute} ${suffix}`;
}

function getTodayString() {
  const date =
    new Date();

  const year =
    date.getFullYear();

  const month =
    String(
      date.getMonth() + 1
    ).padStart(2, '0');

  const day =
    String(
      date.getDate()
    ).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export default function SWFieldworkPage() {
  const { user } =
    useAuth();

  const [
    cases,
    setCases,
  ] = useState<CaseItem[]>(
    []
  );

  const [
    schedules,
    setSchedules,
  ] = useState<Schedule[]>(
    []
  );

  const [
    loadingCases,
    setLoadingCases,
  ] = useState(true);

  const [
    loadingSchedules,
    setLoadingSchedules,
  ] = useState(true);

  const [
    showModal,
    setShowModal,
  ] = useState(false);

  const [
    editingSchedule,
    setEditingSchedule,
  ] = useState<Schedule | null>(null);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState('');

  const [
    success,
    setSuccess,
  ] = useState('');

  const [
    search,
    setSearch,
  ] = useState('');

  const [
    statusFilter,
    setStatusFilter,
  ] = useState('all');

  const [
    form,
    setForm,
  ] = useState<ScheduleForm>({
    caseNumber: '',
    childName: '',

    activityType:
      'home_visit',

    customActivityType: '',

    title: '',
    description: '',

    date: '',
    startTime: '',
    endTime: '',

    location: '',
    notes: '',
  });

  const fetchCases =
    async () => {
      if (!user?.uid) {
        return;
      }

      try {
        setLoadingCases(
          true
        );

        const response =
          await fetch(
            `${API}/get_children.php?uid=${encodeURIComponent(
              user.uid
            )}`,
            {
              method: 'GET',
              headers: {
                Accept:
                  'application/json',
              },
            }
          );

        const data = await response.json();
          if (Array.isArray(data?.children)) {
            setCases(data.children);
          } else if (Array.isArray(data?.data)) {
            setCases(data.data);
          } else if (Array.isArray(data)) {
            setCases(data);
          } else {
            setCases([]);
          }

          if (!response.ok || data?.success === false) {
            throw new Error(
              data?.message ||
              data?.error ||
              `Unable to load assigned cases (${response.status}).`
            );
          }
      } catch (err) {
        console.error(
          'Fetch assigned cases error:',
          err
        );

        setCases([]);
      } finally {
        setLoadingCases(
          false
        );
      }
    };

  const fetchSchedules =
    async () => {
      if (!user?.uid) {
        return;
      }

      try {
        setLoadingSchedules(
          true
        );

        const response =
          await fetch(
            `${API}/get_case_schedules.php?uid=${encodeURIComponent(
              user.uid
            )}`,
            {
              method: 'GET',
              headers: {
                Accept:
                  'application/json',
              },
            }
          );

        const data =
          await response.json();

        if (
          Array.isArray(data)
        ) {
          setSchedules(data);
        } else if (
          Array.isArray(
            data?.data
          )
        ) {
          setSchedules(
            data.data
          );
        } else {
          setSchedules([]);
        }
      } catch (err) {
        console.error(
          'Fetch schedules error:',
          err
        );

        setSchedules([]);
      } finally {
        setLoadingSchedules(
          false
        );
      }
    };

  useEffect(() => {
    if (!user?.uid) {
      return;
    }

    fetchCases();
    fetchSchedules();
  }, [user?.uid]);

  const handleCaseSelect = (
    caseNumber: string
  ) => {
    const selected =
      cases.find(
        (item) =>
          item.case_number ===
          caseNumber
      );

    setForm(
      (previous) => ({
        ...previous,

        caseNumber,

        childName:
          selected?.full_name ||
          selected?.child_name ||
          selected?.name ||
          '',
      })
    );
  };

  const resetForm =
    () => {
      setForm({
        caseNumber: '',
        childName: '',

        activityType:
          'home_visit',

        customActivityType: '',

        title: '',
        description: '',

        date: '',
        startTime: '',
        endTime: '',

        location: '',
        notes: '',
      });
    };

  const closeModal =
    () => {
      if (saving) {
        return;
      }

      setShowModal(
        false
      );

      setEditingSchedule(null);
      resetForm();
    };

  const handleEditSchedule = (schedule: Schedule) => {
    if (normalizeStatus(schedule.status) !== 'scheduled') {
      setError('Only scheduled activities can be edited.');
      return;
    }

    const knownActivity = Object.keys(activityTypeLabels).find(
      (key) =>
        normalizeStatus(key) === normalizeStatus(schedule.activity_type)
    );

    setEditingSchedule(schedule);

    setForm({
      caseNumber: schedule.case_number || '',
      childName: schedule.child_name || '',
      activityType: (knownActivity || 'other') as ActivityType,
      customActivityType: knownActivity
        ? ''
        : schedule.activity_type || '',
      title: schedule.title || '',
      description: schedule.description || '',
      date: schedule.scheduled_date || '',
      startTime: schedule.start_time
        ? schedule.start_time.slice(0, 5)
        : '',
      endTime: schedule.end_time
        ? schedule.end_time.slice(0, 5)
        : '',
      location: schedule.location || '',
      notes: schedule.notes || '',
    });

    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handleUpdateSchedule = async () => {
    setError('');
    setSuccess('');

    if (!editingSchedule) {
      setError('No schedule selected for editing.');
      return;
    }

    if (!form.date) {
      setError('Please select a schedule date.');
      return;
    }

    if (form.date < getTodayString()) {
      setError('The schedule date cannot be in the past.');
      return;
    }

    if (!form.title.trim()) {
      setError('Please enter a schedule title.');
      return;
    }

    if (
      form.activityType === 'other' &&
      !form.customActivityType.trim()
    ) {
      setError('Please specify the activity type.');
      return;
    }

    if (
      form.startTime &&
      form.endTime &&
      form.endTime <= form.startTime
    ) {
      setError('End time must be later than the start time.');
      return;
    }

    const activityType =
      form.activityType === 'other'
        ? form.customActivityType.trim()
        : form.activityType;

    try {
      setSaving(true);

      const response = await fetch(
        `${API}/update_case_schedule.php`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            uid: user?.uid,
            schedule_id: editingSchedule.id,
            activity_type: activityType,
            title: form.title.trim(),
            description: form.description.trim(),
            scheduled_date: form.date,
            start_time: form.startTime || '',
            end_time: form.endTime || '',
            location: form.location.trim(),
            notes: form.notes.trim(),
          }),
        }
      );

      const raw = await response.text();
      let data: any = null;

      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        throw new Error(
          `Update request returned an invalid server response (${response.status}).`
        );
      }

      if (!response.ok || data?.success === false) {
        throw new Error(
          data?.message ||
            data?.error ||
            'Unable to update the case schedule.'
        );
      }

      setSuccess('Case schedule updated successfully.');
      setShowModal(false);
      setEditingSchedule(null);
      resetForm();
      await fetchSchedules();
    } catch (err) {
      console.error('Update schedule error:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to update the case schedule.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit =
    async () => {
      setError('');
      setSuccess('');

      if (
        !form.caseNumber
      ) {
        setError(
          'Please select a case.'
        );

        return;
      }

      if (!form.date) {
        setError(
          'Please select a schedule date.'
        );

        return;
      }

      if (
        form.date <
        getTodayString()
      ) {
        setError(
          'The schedule date cannot be in the past.'
        );

        return;
      }

      if (
        !form.title.trim()
      ) {
        setError(
          'Please enter a schedule title.'
        );

        return;
      }

      if (
        form.activityType ===
          'other' &&
        !form.customActivityType.trim()
      ) {
        setError(
          'Please specify the activity type.'
        );

        return;
      }

      if (
        form.startTime &&
        form.endTime &&
        form.endTime <=
          form.startTime
      ) {
        setError(
          'End time must be later than the start time.'
        );

        return;
      }

      const activityType =
        form.activityType ===
        'other'
          ? form.customActivityType.trim()
          : form.activityType;

      try {
        setSaving(true);

        const response =
          await fetch(
            `${API}/submit_case_schedule.php`,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Accept:
                  'application/json',
              },

              body: JSON.stringify({
                uid: user?.uid,

                case_number:
                  form.caseNumber,

                child_name:
                  form.childName,

                activity_type:
                  activityType,

                title:
                  form.title.trim(),

                description:
                  form.description.trim(),

                scheduled_date:
                  form.date,

                start_time:
                  form.startTime ||
                  null,

                end_time:
                  form.endTime ||
                  null,

                location:
                  form.location.trim(),

                notes:
                  form.notes.trim(),
              }),
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          data?.success === false
        ) {
          throw new Error(
            data?.error ||
              'Unable to create the case schedule.'
          );
        }

        setSuccess(
          'Case schedule created successfully.'
        );

        setShowModal(
          false
        );

        resetForm();

        await fetchSchedules();
      } catch (err) {
        console.error(
          'Create schedule error:',
          err
        );

        setError(
          err instanceof Error
            ? err.message
            : 'Unable to create the case schedule.'
        );
      } finally {
        setSaving(false);
      }
    };

    const handleScheduleStatusUpdate = async (
    scheduleId: number,
    newStatus: 'completed' | 'cancelled'
  ) => {
    setError('');
    setSuccess('');

    const statusLabel =
      newStatus === 'completed'
        ? 'Completed'
        : 'Cancelled';

    const confirmed = window.confirm(
      `Are you sure you want to mark this schedule as ${statusLabel}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setSaving(true);

      const response = await fetch(
        `${API}/update_case_schedule_status.php`,
        {
          method: 'POST',

          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },

          body: JSON.stringify({
            uid: user?.uid,
            schedule_id: scheduleId,
            status: newStatus,
          }),
        }
      );

      const data = await response.json();

      if (
        !response.ok ||
        data?.success === false
      ) {
        throw new Error(
          data?.message ||
            data?.error ||
            'Unable to update the schedule status.'
        );
      }

      setSuccess(
        `Schedule marked as ${statusLabel} successfully.`
      );

      await fetchSchedules();

    } catch (err) {

      console.error(
        'Update schedule status error:',
        err
      );

      setError(
        err instanceof Error
          ? err.message
          : 'Unable to update the schedule status.'
      );

    } finally {

      setSaving(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId: number) => {
    setError('');
    setSuccess('');

    const confirmed = window.confirm(
      'Are you sure you want to delete this case schedule?\n\nThis action cannot be undone.'
    );

    if (!confirmed) {
      return;
    }

    try {
      setSaving(true);

      const response = await fetch(
        `${API}/delete_case_schedule.php`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            uid: user?.uid,
            schedule_id: scheduleId,
          }),
        }
      );

      const raw = await response.text();
      let data: any = null;

      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        throw new Error(
          `Delete request returned an invalid server response (${response.status}).`
        );
      }

      if (!response.ok || data?.success === false) {
        throw new Error(
          data?.message ||
            data?.error ||
            'Unable to delete the schedule.'
        );
      }

      setSuccess('Case schedule deleted successfully.');
      await fetchSchedules();
    } catch (err) {
      console.error('Delete schedule error:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to delete the schedule.'
      );
    } finally {
      setSaving(false);
    }
  };

  const filteredSchedules =
    useMemo(() => {
      const query =
        search
          .trim()
          .toLowerCase();

      return schedules.filter(
        (schedule) => {
          const matchesSearch =
            !query ||
            String(
              schedule.case_number ||
                ''
            )
              .toLowerCase()
              .includes(query) ||
            String(
              schedule.child_name ||
                ''
            )
              .toLowerCase()
              .includes(query) ||
            String(
              schedule.title ||
                ''
            )
              .toLowerCase()
              .includes(query) ||
            formatActivityType(
              schedule.activity_type
            )
              .toLowerCase()
              .includes(query);

          const normalizedStatus =
            normalizeStatus(
              schedule.status
            );

          const matchesStatus =
            statusFilter ===
              'all' ||
            normalizedStatus ===
              normalizeStatus(
                statusFilter
              );

          return (
            matchesSearch &&
            matchesStatus
          );
        }
      );
    }, [
      schedules,
      search,
      statusFilter,
    ]);

  const sortedSchedules =
    useMemo(() => {
      return [
        ...filteredSchedules,
      ].sort((a, b) => {
        const dateA =
          `${a.scheduled_date} ${
            a.start_time ||
            '00:00:00'
          }`;

        const dateB =
          `${b.scheduled_date} ${
            b.start_time ||
            '00:00:00'
          }`;

        return dateA.localeCompare(
          dateB
        );
      });
    }, [
      filteredSchedules,
    ]);

  const scheduledCount =
    schedules.filter(
      (item) =>
        normalizeStatus(
          item.status
        ) === 'scheduled'
    ).length;

  const completedCount =
    schedules.filter(
      (item) =>
        normalizeStatus(
          item.status
        ) === 'completed'
    ).length;

  const cancelledCount =
    schedules.filter(
      (item) =>
        normalizeStatus(
          item.status
        ) === 'cancelled'
    ).length;

  return (
    <AppLayout
      title="Case Scheduling"
      breadcrumb={[
        'Social Worker',
        'Case Scheduling',
      ]}
    >
      <div className="max-w-7xl mx-auto space-y-6">



        <section className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8">

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">

            <div className="flex items-start gap-4">

              <div className="w-14 h-14 rounded-2xl bg-teal-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-calendar-schedule-line text-teal-600 text-2xl" />
              </div>

              <div>

                <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                  Case Scheduling
                </h1>

                <p className="text-sm text-gray-500 mt-2">
                  Schedule child visits, interventions,
                  follow-ups, meetings, and other case
                  activities.
                </p>

              </div>

            </div>

            <button
              type="button"
              onClick={() => {
                setError('');
                setSuccess('');
                resetForm();
                setShowModal(true);
              }}
              className="bg-teal-500 hover:bg-teal-600 text-white px-5 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 shadow-sm"
            >
              <i className="ri-add-line text-lg" />
              Schedule Activity
            </button>

          </div>

        </section>



        {success && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-start gap-3">

            <i className="ri-checkbox-circle-line text-green-600 text-xl" />

            <div>
              <p className="text-sm font-semibold text-green-800">
                Success
              </p>

              <p className="text-sm text-green-700 mt-1">
                {success}
              </p>
            </div>

            <button
              type="button"
              onClick={() =>
                setSuccess('')
              }
              className="ml-auto text-green-500"
            >
              <i className="ri-close-line" />
            </button>

          </div>
        )}

        {error && !showModal && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3">

            <i className="ri-error-warning-line text-red-600 text-xl" />

            <div>
              <p className="text-sm font-semibold text-red-800">
                Unable to process request
              </p>

              <p className="text-sm text-red-700 mt-1">
                {error}
              </p>
            </div>

            <button
              type="button"
              onClick={() =>
                setError('')
              }
              className="ml-auto text-red-500"
            >
              <i className="ri-close-line" />
            </button>

          </div>
        )}



        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">

          <SummaryCard
            icon="ri-calendar-line"
            label="Scheduled"
            value={scheduledCount}
            iconClass="bg-blue-50 text-blue-600"
          />

          <SummaryCard
            icon="ri-checkbox-circle-line"
            label="Completed"
            value={completedCount}
            iconClass="bg-green-50 text-green-600"
          />

          <SummaryCard
            icon="ri-close-circle-line"
            label="Cancelled"
            value={cancelledCount}
            iconClass="bg-red-50 text-red-600"
          />

        </section>



        <section className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">

          <div className="p-5 md:p-6 border-b border-gray-100">

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">

              <div>

                <h2 className="text-lg font-bold text-gray-900">
                  Case Schedule
                </h2>

                <p className="text-sm text-gray-500 mt-1">
                  View and manage your scheduled case activities.
                </p>

              </div>

              <div className="flex flex-col sm:flex-row gap-3">



                <div className="relative">

                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />

                  <input
                    value={search}
                    onChange={(e) =>
                      setSearch(
                        e.target.value
                      )
                    }
                    placeholder="Search case or child..."
                    className="w-full sm:w-64 border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm outline-none focus:border-teal-500"
                  />

                </div>



                <select
                  value={
                    statusFilter
                  }
                  onChange={(e) =>
                    setStatusFilter(
                      e.target.value
                    )
                  }
                  className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 bg-white"
                >
                  <option value="all">
                    All Status
                  </option>

                  <option value="scheduled">
                    Scheduled
                  </option>

                  <option value="completed">
                    Completed
                  </option>

                  <option value="cancelled">
                    Cancelled
                  </option>

                  <option value="rescheduled">
                    Rescheduled
                  </option>
                </select>

              </div>

            </div>

          </div>



          {loadingSchedules && (
            <div className="p-12 text-center">

              <i className="ri-loader-4-line animate-spin text-3xl text-teal-500" />

              <p className="text-sm text-gray-500 mt-3">
                Loading case schedules...
              </p>

            </div>
          )}



          {!loadingSchedules &&
            sortedSchedules.length ===
              0 && (
              <div className="p-12 text-center">

                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto">

                  <i className="ri-calendar-close-line text-gray-400 text-2xl" />

                </div>

                <h3 className="text-lg font-bold text-gray-800 mt-4">
                  No schedules found
                </h3>

                <p className="text-sm text-gray-500 mt-2">
                  {search ||
                  statusFilter !==
                    'all'
                    ? 'Try changing your search or filter.'
                    : 'Create your first case activity schedule.'}
                </p>

                {!search &&
                  statusFilter ===
                    'all' && (
                    <button
                      type="button"
                      onClick={() =>
                        setShowModal(
                          true
                        )
                      }
                      className="mt-5 bg-teal-500 hover:bg-teal-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold"
                    >
                      <i className="ri-add-line mr-2" />
                      Schedule Activity
                    </button>
                  )}

              </div>
            )}



          {!loadingSchedules &&
            sortedSchedules.length >
              0 && (
              <div className="divide-y divide-gray-100">

                {sortedSchedules.map(
                  (schedule) => (
                    <ScheduleCard
                      key={schedule.id}
                      schedule={schedule}
                      onStatusUpdate={
                        handleScheduleStatusUpdate
                      }
                      onDelete={
                        handleDeleteSchedule
                      }
                      onEdit={
                        handleEditSchedule
                      }
                      isSaving={saving}
                    />
                  )
                )}

              </div>
            )}

        </section>



        {showModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">

            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">



              <div className="sticky top-0 bg-white z-10 px-6 py-5 border-b border-gray-100 flex items-center justify-between">

                <div>

                  <h2 className="text-xl font-bold text-gray-900">
                    {editingSchedule
                      ? 'Edit Case Activity'
                      : 'Schedule Case Activity'}
                  </h2>

                  <p className="text-sm text-gray-500 mt-1">
                    {editingSchedule
                      ? 'Update the details of your scheduled case activity.'
                      : 'Create a schedule for an assigned case.'}
                  </p>

                </div>

                <button
                  type="button"
                  onClick={
                    closeModal
                  }
                  disabled={saving}
                  className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500"
                >
                  <i className="ri-close-line text-xl" />
                </button>

              </div>



              <div className="p-6 space-y-5">



                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">

                    <i className="ri-error-warning-line text-red-600 text-lg" />

                    <p className="text-sm text-red-700">
                      {error}
                    </p>

                  </div>
                )}



                <div>

                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Assigned Case
                    <span className="text-red-500 ml-1">
                      *
                    </span>
                  </label>

                  <select
                    value={
                      form.caseNumber
                    }
                    onChange={(e) =>
                      handleCaseSelect(
                        e.target.value
                      )
                    }
                    disabled={
                      loadingCases ||
                      saving ||
                      !!editingSchedule
                    }
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-white outline-none focus:border-teal-500"
                  >

                    <option value="">
                      {loadingCases
                        ? 'Loading assigned cases...'
                        : 'Select assigned case'}
                    </option>

                    {cases.map(
                      (item) => (
                        <option
                          key={
                            item.case_number
                          }
                          value={
                            item.case_number
                          }
                        >
                          {item.case_number}
                          {' — '}
                          {item.full_name ||
                            item.child_name ||
                            item.name ||
                            'Child'}
                        </option>
                      )
                    )}

                  </select>

                  {!loadingCases &&
                    cases.length ===
                      0 && (
                      <p className="text-xs text-amber-600 mt-2">
                        No assigned cases are available.
                      </p>
                    )}

                  {form.childName && (
                    <div className="mt-2 bg-gray-50 rounded-xl px-4 py-3">

                      <p className="text-xs text-gray-400 uppercase font-semibold">
                        Child
                      </p>

                      <p className="text-sm font-bold text-gray-800 mt-1">
                        {form.childName}
                      </p>

                    </div>
                  )}

                </div>



                <div>

                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Activity Type
                    <span className="text-red-500 ml-1">
                      *
                    </span>
                  </label>

                  <select
                    value={
                      form.activityType
                    }
                    onChange={(e) =>
                      setForm(
                        (previous) => ({
                          ...previous,

                          activityType:
                            e.target.value as ActivityType,

                          customActivityType:
                            e.target.value ===
                            'other'
                              ? previous.customActivityType
                              : '',
                        })
                      )
                    }
                    disabled={saving}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-white outline-none focus:border-teal-500"
                  >

                    {Object.entries(
                      activityTypeLabels
                    ).map(
                      ([
                        value,
                        label,
                      ]) => (
                        <option
                          key={
                            value
                          }
                          value={
                            value
                          }
                        >
                          {label}
                        </option>
                      )
                    )}

                    <option value="other">
                      Other
                    </option>

                  </select>

                </div>



                {form.activityType ===
                  'other' && (
                  <div>

                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Specify Activity Type
                      <span className="text-red-500 ml-1">
                        *
                      </span>
                    </label>

                    <input
                      value={
                        form.customActivityType
                      }
                      onChange={(e) =>
                        setForm(
                          (
                            previous
                          ) => ({
                            ...previous,

                            customActivityType:
                              e.target.value,
                          })
                        )
                      }
                      disabled={saving}
                      placeholder="Enter custom activity type"
                      maxLength={100}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-teal-500"
                    />

                  </div>
                )}



                <div>

                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Schedule Title
                    <span className="text-red-500 ml-1">
                      *
                    </span>
                  </label>

                  <input
                    value={
                      form.title
                    }
                    onChange={(e) =>
                      setForm(
                        (
                          previous
                        ) => ({
                          ...previous,
                          title:
                            e.target.value,
                        })
                      )
                    }
                    disabled={saving}
                    placeholder="Example: Home visit with guardian"
                    maxLength={255}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-teal-500"
                  />

                </div>



                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                  <div>

                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Date
                      <span className="text-red-500 ml-1">
                        *
                      </span>
                    </label>

                    <input
                      type="date"
                      min={
                        getTodayString()
                      }
                      value={
                        form.date
                      }
                      onChange={(e) =>
                        setForm(
                          (
                            previous
                          ) => ({
                            ...previous,
                            date:
                              e.target.value,
                          })
                        )
                      }
                      disabled={saving}
                      className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm outline-none focus:border-teal-500"
                    />

                  </div>

                  <div>

                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Start Time
                    </label>

                    <input
                      type="time"
                      value={
                        form.startTime
                      }
                      onChange={(e) =>
                        setForm(
                          (
                            previous
                          ) => ({
                            ...previous,
                            startTime:
                              e.target.value,
                          })
                        )
                      }
                      disabled={saving}
                      className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm outline-none focus:border-teal-500"
                    />

                  </div>

                  <div>

                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      End Time
                    </label>

                    <input
                      type="time"
                      value={
                        form.endTime
                      }
                      onChange={(e) =>
                        setForm(
                          (
                            previous
                          ) => ({
                            ...previous,
                            endTime:
                              e.target.value,
                          })
                        )
                      }
                      disabled={saving}
                      className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm outline-none focus:border-teal-500"
                    />

                  </div>

                </div>



                <div>

                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Location
                  </label>

                  <div className="relative">

                    <i className="ri-map-pin-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />

                    <input
                      value={
                        form.location
                      }
                      onChange={(e) =>
                        setForm(
                          (
                            previous
                          ) => ({
                            ...previous,
                            location:
                              e.target.value,
                          })
                        )
                      }
                      disabled={saving}
                      placeholder="Address, barangay, office, school, or venue"
                      maxLength={500}
                      className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-3 text-sm outline-none focus:border-teal-500"
                    />

                  </div>

                </div>



                <div>

                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Activity Description
                  </label>

                  <textarea
                    value={
                      form.description
                    }
                    onChange={(e) =>
                      setForm(
                        (
                          previous
                        ) => ({
                          ...previous,
                          description:
                            e.target.value,
                        })
                      )
                    }
                    disabled={saving}
                    rows={4}
                    maxLength={2000}
                    placeholder="Describe the purpose or planned activity..."
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-teal-500 resize-none"
                  />

                </div>



                <div>

                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Notes
                  </label>

                  <textarea
                    value={
                      form.notes
                    }
                    onChange={(e) =>
                      setForm(
                        (
                          previous
                        ) => ({
                          ...previous,
                          notes:
                            e.target.value,
                        })
                      )
                    }
                    disabled={saving}
                    rows={3}
                    maxLength={2000}
                    placeholder="Additional instructions or reminders..."
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-teal-500 resize-none"
                  />

                </div>

              </div>



              <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">

                <button
                  type="button"
                  onClick={
                    closeModal
                  }
                  disabled={saving}
                  className="border border-gray-200 text-gray-700 px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={
                    editingSchedule
                      ? handleUpdateSchedule
                      : handleSubmit
                  }
                  disabled={
                    saving ||
                    loadingCases ||
                    cases.length === 0
                  }
                  className="bg-teal-500 hover:bg-teal-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      {editingSchedule
                        ? 'Updating...'
                        : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <i
                        className={
                          editingSchedule
                            ? 'ri-save-line'
                            : 'ri-calendar-check-line'
                        }
                      />
                      {editingSchedule
                        ? 'Update Schedule'
                        : 'Create Schedule'}
                    </>
                  )}
                </button>

              </div>

            </div>

          </div>
        )}

      </div>
    </AppLayout>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  iconClass,
}: {
  icon: string;
  label: string;
  value: number;
  iconClass: string;
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">

      <div className="flex items-center gap-4">

        <div
          className={`w-11 h-11 rounded-xl flex items-center justify-center ${iconClass}`}
        >
          <i
            className={`${icon} text-xl`}
          />
        </div>

        <div>

          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            {label}
          </p>

          <p className="text-2xl font-extrabold text-gray-900 mt-1">
            {value}
          </p>

        </div>

      </div>

    </div>
  );
}

function ScheduleCard({
  schedule,
  onStatusUpdate,
  onDelete,
  onEdit,
  isSaving,
}: {
  schedule: Schedule;
  onStatusUpdate: (
    scheduleId: number,
    newStatus: 'completed' | 'cancelled'
  ) => void;
  onDelete: (scheduleId: number) => void;
  onEdit: (schedule: Schedule) => void;
  isSaving: boolean;
}) {
  const status =
    normalizeStatus(
      schedule.status
    );

  const isToday =
    schedule.scheduled_date ===
    getTodayString();

  return (
    <div className="p-5 md:p-6 hover:bg-gray-50/70 transition">

      <div className="flex flex-col lg:flex-row lg:items-start gap-5">



        <div className="w-12 h-12 rounded-2xl bg-teal-50 flex items-center justify-center flex-shrink-0">

          <i
            className={`${getActivityIcon(
              schedule.activity_type
            )} text-xl text-teal-600`}
          />

        </div>



        <div className="flex-1 min-w-0">

          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">

            <div>

              <div className="flex flex-wrap items-center gap-2">

                <h3 className="text-base font-bold text-gray-900">
                  {schedule.title ||
                    formatActivityType(
                      schedule.activity_type
                    )}
                </h3>

                {isToday && (
                  <span className="text-[11px] px-2 py-1 rounded-full bg-teal-100 text-teal-700 font-semibold">
                    Today
                  </span>
                )}

              </div>

              <p className="text-sm text-teal-600 font-semibold mt-1">
                {formatActivityType(
                  schedule.activity_type
                )}
              </p>

            </div>

            <span
              className={`inline-flex items-center gap-1.5 w-fit text-xs px-3 py-1.5 rounded-full font-semibold ${getStatusStyle(
                schedule.status
              )}`}
            >

              <span className="w-1.5 h-1.5 rounded-full bg-current" />

              {formatStatus(
                schedule.status
              )}

            </span>

          </div>



          <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4">

            <div className="flex items-center gap-2">

              <i className="ri-folder-user-line text-gray-400" />

              <span className="text-sm text-gray-700 font-medium">
                {schedule.case_number}
              </span>

            </div>

            {schedule.child_name && (
              <div className="flex items-center gap-2">

                <i className="ri-user-heart-line text-gray-400" />

                <span className="text-sm text-gray-700">
                  {schedule.child_name}
                </span>

              </div>
            )}

          </div>



          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">

            <div className="bg-gray-50 rounded-xl px-3 py-2.5">

              <div className="flex items-center gap-2">

                <i className="ri-calendar-line text-gray-400" />

                <div>

                  <p className="text-[11px] uppercase font-semibold text-gray-400">
                    Date
                  </p>

                  <p className="text-sm font-semibold text-gray-700">
                    {formatDate(
                      schedule.scheduled_date
                    )}
                  </p>

                </div>

              </div>

            </div>

            <div className="bg-gray-50 rounded-xl px-3 py-2.5">

              <div className="flex items-center gap-2">

                <i className="ri-time-line text-gray-400" />

                <div>

                  <p className="text-[11px] uppercase font-semibold text-gray-400">
                    Time
                  </p>

                  <p className="text-sm font-semibold text-gray-700">

                    {schedule.start_time
                      ? formatTime(
                          schedule.start_time
                        )
                      : 'Time not set'}

                    {schedule.end_time &&
                      ` – ${formatTime(
                        schedule.end_time
                      )}`}

                  </p>

                </div>

              </div>

            </div>

            <div className="bg-gray-50 rounded-xl px-3 py-2.5">

              <div className="flex items-center gap-2">

                <i className="ri-map-pin-line text-gray-400" />

                <div className="min-w-0">

                  <p className="text-[11px] uppercase font-semibold text-gray-400">
                    Location
                  </p>

                  <p className="text-sm font-semibold text-gray-700 truncate">
                    {schedule.location ||
                      'Location not set'}
                  </p>

                </div>

              </div>

            </div>

          </div>



          {schedule.description && (
            <div className="mt-4">

              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Description
              </p>

              <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                {schedule.description}
              </p>

            </div>
          )}



                    {schedule.notes && (
            <div className="mt-3">

              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Notes
              </p>

              <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                {schedule.notes}
              </p>

            </div>
          )}



          <div className="mt-5 pt-4 border-t border-gray-100 flex flex-wrap gap-3">
            {status === 'scheduled' && (
              <>
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() =>
                    onStatusUpdate(
                      schedule.id,
                      'completed'
                    )
                  }
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-checkbox-circle-line text-lg" />
                  Mark Completed
                </button>

                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() =>
                    onStatusUpdate(
                      schedule.id,
                      'cancelled'
                    )
                  }
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-close-circle-line text-lg" />
                  Cancel Schedule
                </button>
              </>
            )}

            {status === 'scheduled' && (
              <button
                type="button"
                disabled={isSaving}
                onClick={() => onEdit(schedule)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 transition text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-edit-line text-lg" />
                Edit
              </button>
            )}
            {status === 'scheduled' && (
              <button
                type="button"
                disabled={isSaving}
                onClick={() => onDelete(schedule.id)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100 hover:text-red-600 hover:border-red-200 transition text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-delete-bin-line text-lg" />
                Delete
              </button>
            )}
          </div>

        </div>

      </div>
    </div>
  );
}